import React, { useEffect, useRef, useState } from "react";
import { Zap } from "lucide-react";

// Dynamic joystick: touch anywhere on the left half of the screen and the stick
// spawns under your thumb. Right side has a dedicated boost button.
export function MobileControls({ input }) {
  const [stick, setStick] = useState(null); // { ox, oy, kx, ky }
  const touchId = useRef(null);
  const R = 68;

  const setDir = (dx, dy) => {
    const d = Math.hypot(dx, dy);
    const cl = d > R ? R / d : 1;
    input.joy.active = true;
    input.joy.dx = (dx * cl) / R;
    input.joy.dy = (dy * cl) / R;
    return { kx: dx * cl, ky: dy * cl };
  };

  const onStart = (e) => {
    if (touchId.current !== null) return;
    const t = e.changedTouches[0];
    touchId.current = t.identifier;
    const k = setDir(0, 0);
    setStick({ ox: t.clientX, oy: t.clientY, ...k });
  };
  const onMove = (e) => {
    for (const t of e.changedTouches) {
      if (t.identifier !== touchId.current || !stick) continue;
      const k = setDir(t.clientX - stick.ox, t.clientY - stick.oy);
      setStick((s) => (s ? { ...s, ...k } : s));
    }
  };
  const onEnd = (e) => {
    for (const t of e.changedTouches) {
      if (t.identifier !== touchId.current) continue;
      touchId.current = null;
      input.joy.active = false; input.joy.dx = 0; input.joy.dy = 0;
      setStick(null);
    }
  };

  useEffect(() => () => { input.joy.active = false; input.boostBtn = false; }, [input]);

  const BASE = 150, KNOB = 66;
  return (
    <div className="absolute inset-0 z-40 select-none">
      {/* left-half touch zone spawns the joystick */}
      <div
        className="absolute inset-y-0 left-0 w-1/2 pointer-events-auto"
        style={{ touchAction: "none" }}
        onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} onTouchCancel={onEnd}
        data-testid="mobile-joy-zone"
      />
      {stick && (
        <div className="absolute pointer-events-none" style={{ left: stick.ox - BASE / 2, top: stick.oy - BASE / 2, width: BASE, height: BASE }} data-testid="mobile-joystick">
          <div className="absolute inset-0 rounded-full border-2 border-cyan-400/50 bg-slate-900/45 backdrop-blur" />
          <div
            className="absolute rounded-full bg-gradient-to-br from-cyan-400 to-blue-500"
            style={{ width: KNOB, height: KNOB, left: (BASE - KNOB) / 2 + stick.kx, top: (BASE - KNOB) / 2 + stick.ky, boxShadow: "0 0 24px rgba(0,229,255,0.8)" }}
          />
        </div>
      )}
      {/* boost button */}
      <button
        className="absolute bottom-28 right-6 pointer-events-auto rounded-full bg-gradient-to-br from-amber-400 to-orange-600 flex flex-col items-center justify-center shadow-[0_0_28px_rgba(255,149,0,0.7)] active:scale-90 transition-transform"
        style={{ width: 112, height: 112, touchAction: "none" }}
        onTouchStart={(e) => { e.preventDefault(); input.boostBtn = true; }}
        onTouchEnd={(e) => { e.preventDefault(); input.boostBtn = false; }}
        onTouchCancel={() => { input.boostBtn = false; }}
        data-testid="boost-button"
      >
        <Zap size={40} className="text-black" fill="black" />
        <span className="text-black font-black text-[10px] tracking-widest -mt-0.5">BOOST</span>
      </button>
    </div>
  );
}
